import 'components/list/table.css';
import { prepareResponsiveTable } from 'components/list/responsiveTable';
import React, { useEffect, useId, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import FormDateRangePicker from 'components/date-picker/DateRangePicker';
import CustomSelect from 'components/custom-select';

export { CustomSelect };

const buttonVariants = {
  primary:
    'border-transparent bg-[#0f766e] text-white shadow-sm hover:bg-[#0b625c] focus-visible:ring-[#0f766e]/25',
  secondary:
    'border-[#d9e2e6] bg-white text-[#344054] hover:border-[#b9c9ce] hover:bg-[#fbfdfd] focus-visible:ring-slate-300/35',
  danger:
    'border-[#f1d4d2] bg-[#fff9f8] text-[#b54743] hover:border-[#e6b5b1] hover:bg-[#fff3f1] focus-visible:ring-red-300/30',
  quiet:
    'border-transparent bg-transparent text-[#536273] hover:bg-[#f1f5f6] hover:text-[#263747] focus-visible:ring-slate-300/35',
};

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  loading = false,
  className = '',
  type = 'button',
  disabled = false,
  ...props
}) {
  const sizeClass =
    size === 'sm' ? 'min-h-8 gap-1.5 px-2.5 text-xs' : 'min-h-10 gap-2 px-3.5 text-[13px]';

  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`inline-flex items-center justify-center rounded-lg border font-medium transition duration-150 focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50 ${sizeClass} ${buttonVariants[variant] || buttonVariants.secondary} ${className}`}
      {...props}
    >
      {loading ? (
        <span
          className='h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current/25 border-t-current'
          aria-hidden='true'
        />
      ) : (
        Icon && <Icon className='h-4 w-4 shrink-0' aria-hidden='true' />
      )}
      {children}
    </button>
  );
}

export function IconButton({ label, children, className = '', variant = 'default', ...props }) {
  const variantClass =
    variant === 'primary'
      ? 'border-[#0f766e] bg-[#0f766e] text-white hover:border-[#0b625c] hover:bg-[#0b625c] hover:text-white'
      : 'border-transparent text-[#64748b] hover:border-[#e2e8eb] hover:bg-white hover:text-[#253447]';

  return (
    <button
      type='button'
      aria-label={label}
      className={`inline-grid h-9 w-9 place-items-center rounded-lg border transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-700/10 ${variantClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Card({ as: Component = 'section', children, className = '', ...props }) {
  return (
    <Component
      className={`min-w-0 rounded-xl border border-[#e3e9ed] bg-white shadow-[0_2px_8px_rgba(28,46,63,0.025)] ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
}

export function CardBody({ children, className = '', ...props }) {
  return (
    <div className={`min-w-0 p-3 sm:p-4 ${className}`} {...props}>
      {children}
    </div>
  );
}

export function CardTitle({ as: Component = 'h2', children, className = '', ...props }) {
  return (
    <Component
      className={`text-[15px] font-semibold tracking-[-0.015em] text-[#253246] ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
}

export function CardHeading({ title, description, action, className = '' }) {
  return (
    <div className={`flex items-start justify-between gap-4 ${className}`}>
      <div className='min-w-0'>
        <CardTitle>{title}</CardTitle>
        {description && <p className='mt-1.5 text-xs leading-5 text-[#718096]'>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function ListToolbar({
  as: Component = 'div',
  flush = false,
  children,
  className = '',
  ...props
}) {
  return (
    <Component
      className={`flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-[#edf1f3] ${flush ? '' : 'p-4 sm:p-5'} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
}

export function FilterPanel({ variant = 'default', children, className = '', ...props }) {
  const variantClass =
    variant === 'card'
      ? 'rounded-lg border border-[#e6ecef] bg-[#f8fafb]'
      : 'border-t border-[#edf1f3]';

  return (
    <div
      className={`grid min-w-0 grid-cols-1 gap-4 ${variantClass} p-4 sm:px-5 sm:py-4 [&_label]:text-xs [&_label]:font-medium [&_label]:leading-5 [&_label]:text-[#445268] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

const defaultPageSizeOptions = [25, 50, 75, 100].map((value) => ({
  value,
  label: String(value),
}));

export function ListMeta({
  pageSize,
  onPageSizeChange,
  pageSizeOptions = defaultPageSizeOptions,
  label = '',
  ariaLabel = 'Jumlah baris per halaman',
  className = '',
}) {
  const selectedOption = pageSizeOptions.find(
    (option) => Number(option.value) === Number(pageSize)
  ) || {
    value: pageSize,
    label: String(pageSize),
  };

  return (
    <div
      className={`flex min-w-0 flex-wrap items-center ${label ? 'justify-between' : 'justify-end'} gap-3 border-b border-[#edf1f3] px-4 py-1 sm:px-5 ${className}`}
    >
      {label && <span className='text-[11px] text-[#778698]'>{label}</span>}
      <div className='flex min-w-0 items-center gap-2'>
        <span className='shrink-0 text-[10px] font-medium text-[#758396]'>Baris per halaman</span>
        <CustomSelect
          ariaLabel={ariaLabel}
          value={selectedOption}
          onChange={onPageSizeChange}
          options={pageSizeOptions}
          className='w-[76px] shrink-0'
          compact
          searchable={false}
        />
      </div>
    </div>
  );
}

export function DataTable({
  children,
  className = '',
  compact = false,
  sticky = false,
  zebra = false,
  ...props
}) {
  return (
    <table
      // Fixed layouts can make switches and action groups spill into adjacent columns.
      // Keep intrinsic column sizing even when a legacy caller supplies table-fixed.
      className={`cms-data-table w-full border-collapse text-left text-xs [&_th]:border-b [&_th]:border-[#e8edf0] [&_th]:bg-[#f7f9fa] [&_th]:px-3 [&_th]:py-3 [&_th]:text-[10px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.06em] [&_th]:text-[#778596] [&_td]:border-b [&_td]:border-[#edf1f3] [&_td]:px-3 [&_td]:py-3 [&_td]:align-middle [&_td]:text-xs [&_td]:text-[#536276] [&_tbody_tr:last-child_td]:border-b-0 [&_tbody_tr]:transition-colors [&_tbody_tr:hover]:bg-[#fbfdfd] ${compact ? '[&_th]:py-2 [&_td]:py-2' : ''} ${sticky ? '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10' : ''} ${zebra ? '[&_tbody_tr:nth-child(even)]:bg-[#fafcfc]' : ''} ${className}`}
      {...props}
    >
      {prepareResponsiveTable(children)}
    </table>
  );
}

export function MenuPageHeader({ eyebrow, title, description, icon, action, className = '' }) {
  return (
    <div className={`flex flex-wrap items-end justify-between gap-4 ${className}`}>
      <div className='flex min-w-0 items-start gap-3'>
        {icon && (
          <span className='mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#edf8f6] text-[#0f766e] [&>svg]:h-4 [&>svg]:w-4'>
            {icon}
          </span>
        )}
        <div className='min-w-0'>
          {eyebrow && (
            <p className='text-[10px] font-semibold tracking-[0.12em] text-[#8794a1]'>{eyebrow}</p>
          )}
          <h1 className='mt-1 text-xl font-semibold tracking-[-0.02em] text-[#253246] sm:text-[22px]'>
            {title}
          </h1>
          {description && (
            <p className='mt-1.5 max-w-2xl text-xs leading-5 text-[#718096]'>{description}</p>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

const statusMap = {
  Berhasil: 'border-[#d2eee5] bg-[#f1faf6] text-[#287454]',
  Diproses: 'border-[#f1e4c7] bg-[#fffaf0] text-[#9a6a1e]',
  Gagal: 'border-[#f0d8d7] bg-[#fff6f5] text-[#ac4f4b]',
  Draf: 'border-[#e2e7ed] bg-[#f7f9fb] text-[#687588]',
  Terkirim: 'border-[#d2eee5] bg-[#f1faf6] text-[#287454]',
  Menunggu: 'border-[#f1e4c7] bg-[#fffaf0] text-[#9a6a1e]',
  Baru: 'border-[#f1e4c7] bg-[#fffaf0] text-[#9a6a1e]',
  Dibaca: 'border-[#e2e7ed] bg-[#f7f9fb] text-[#687588]',
};

export function StatusBadge({ status, className = '' }) {
  const classes = statusMap[status] || statusMap.Draf;

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${classes} ${className}`}
    >
      {status}
    </span>
  );
}

const toggleToneStyles = {
  primary: {
    active: 'border-[#0f766e] bg-[#0f766e]',
    inactive: 'border-[#cad5db] bg-[#e7ecef]',
  },
  success: {
    active: 'border-emerald-600 bg-emerald-600',
    inactive: 'border-emerald-200 bg-emerald-50',
  },
  info: {
    active: 'border-sky-600 bg-sky-600',
    inactive: 'border-sky-200 bg-sky-50',
  },
  warning: {
    active: 'border-amber-500 bg-amber-500',
    inactive: 'border-amber-200 bg-amber-50',
  },
  indigo: {
    active: 'border-indigo-600 bg-indigo-600',
    inactive: 'border-indigo-200 bg-indigo-50',
  },
  cyan: {
    active: 'border-cyan-600 bg-cyan-600',
    inactive: 'border-cyan-200 bg-cyan-50',
  },
  neutral: {
    active: 'border-slate-600 bg-slate-600',
    inactive: 'border-[#cad5db] bg-[#e7ecef]',
  },
};

export function Toggle({ checked, onChange, label, disabled = false, id, name, tone = 'primary' }) {
  const toneStyle = toggleToneStyles[tone] || toggleToneStyles.primary;

  return (
    <button
      id={id}
      name={name}
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-700/10 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? toneStyle.active : toneStyle.inactive}`}
    >
      <span
        className={`h-[18px] w-[18px] rounded-full bg-white shadow-sm transition ${checked ? 'translate-x-[20px]' : 'translate-x-[3px]'}`}
      />
    </button>
  );
}

export function SegmentedControl({
  label,
  ariaLabel,
  value,
  options,
  onChange,
  fullWidth = false,
  size,
  compact = size === 'sm',
  disabled = false,
  className = '',
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      {label && <p className='mb-2 text-xs font-medium text-[#445268]'>{label}</p>}
      <div
        role='group'
        aria-label={ariaLabel || label}
        className={`inline-flex max-w-full rounded-lg border border-[#dfe7ea] bg-[#f5f8f9] p-1 ${fullWidth ? 'w-full' : ''}`}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type='button'
            disabled={disabled}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={`min-h-7 min-w-0 whitespace-nowrap rounded-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700/20 disabled:cursor-not-allowed disabled:opacity-50 ${compact ? 'px-2 py-1 text-[10px]' : 'px-3 py-2 text-xs'} ${fullWidth ? 'flex-1' : ''} ${value === option.value ? 'bg-[#0f766e] font-semibold text-white shadow-sm' : 'text-[#69788a] hover:text-[#263747]'}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Tabs({
  children,
  label = 'Tab',
  options = [],
  value,
  defaultValue,
  onChange,
  className = '',
}) {
  const tabsId = useId();
  const panels = React.Children.toArray(children);
  const [internalValue, setInternalValue] = useState(defaultValue ?? options[0]?.value);
  const selectedValue = value ?? internalValue;
  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === selectedValue)
  );

  const selectTab = (nextValue) => {
    if (value === undefined) setInternalValue(nextValue);
    onChange?.(nextValue);
  };

  const handleKeyDown = (event, index) => {
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % options.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + options.length) % options.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = options.length - 1;
    else return;

    event.preventDefault();
    selectTab(options[nextIndex]?.value);
    document.getElementById(`${tabsId}-tab-${nextIndex}`)?.focus();
  };

  return (
    <div className={`min-w-0 ${className}`}>
      <div
        className='flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-lg bg-[#edf2f1] p-1'
        role='tablist'
        aria-label={label}
      >
        {options.map((option, index) => (
          <button
            key={option.value}
            id={`${tabsId}-tab-${index}`}
            type='button'
            role='tab'
            aria-selected={activeIndex === index}
            aria-controls={`${tabsId}-panel-${index}`}
            tabIndex={activeIndex === index ? 0 : -1}
            onClick={() => selectTab(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`min-h-8 shrink-0 rounded-md px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f766e] focus-visible:ring-offset-1 ${activeIndex === index ? 'bg-white text-[#0f766e] shadow-sm' : 'text-[#718087] hover:text-[#26363d]'}`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div
        id={`${tabsId}-panel-${activeIndex}`}
        className='min-w-0 pt-3'
        role='tabpanel'
        aria-labelledby={`${tabsId}-tab-${activeIndex}`}
        tabIndex={0}
      >
        {panels[activeIndex]}
      </div>
    </div>
  );
}

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const monthIndexes = Object.fromEntries(months.map((month, index) => [month.toLowerCase(), index]));

const dateFromLabel = (day, month, year) => {
  const monthIndex = monthIndexes[String(month).toLowerCase().replace('.', '')];
  if (monthIndex === undefined) return null;
  return new Date(Number(year), monthIndex, Number(day));
};

const parseRangeLabel = (value) => {
  if (value && typeof value === 'object') {
    const startDate = value.startDate ?? value.start ?? null;
    const endDate = value.endDate ?? value.end ?? null;
    return {
      startDate: startDate ? dayjs(startDate).toDate() : null,
      endDate: endDate ? dayjs(endDate).toDate() : null,
    };
  }

  if (typeof value !== 'string' || !value.trim()) return { startDate: null, endDate: null };

  const sameMonth = value.match(/^\s*(\d{1,2})\s*[–-]\s*(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})\s*$/);
  if (sameMonth) {
    return {
      startDate: dateFromLabel(sameMonth[1], sameMonth[3], sameMonth[4]),
      endDate: dateFromLabel(sameMonth[2], sameMonth[3], sameMonth[4]),
    };
  }

  const crossMonth = value.match(
    /^\s*(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})\s*[–-]\s*(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})\s*$/
  );
  if (crossMonth) {
    return {
      startDate: dateFromLabel(crossMonth[1], crossMonth[2], crossMonth[3]),
      endDate: dateFromLabel(crossMonth[4], crossMonth[5], crossMonth[6]),
    };
  }

  const singleDate = value.match(/^\s*(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})\s*$/);
  const date = singleDate ? dateFromLabel(singleDate[1], singleDate[2], singleDate[3]) : null;
  return { startDate: date, endDate: date };
};

const formatRangeLabel = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const startMonth = months[start.month()];
  const endMonth = months[end.month()];
  if (start.isSame(end, 'day')) return `${start.date()} ${startMonth} ${start.year()}`;
  if (start.isSame(end, 'month')) {
    return `${start.date()}–${end.date()} ${endMonth} ${end.year()}`;
  }
  return `${start.date()} ${startMonth} ${start.year()} – ${end.date()} ${endMonth} ${end.year()}`;
};

export function DateRangePicker(props) {
  if (Object.prototype.hasOwnProperty.call(props, 'date')) {
    return <FormDateRangePicker {...props} />;
  }

  const {
    value,
    onChange,
    onDateChange,
    label = 'Tanggal transaksi',
    placeholder = 'Pilih rentang tanggal',
    ...pickerProps
  } = props;
  const [selectedRange, setSelectedRange] = useState(() => parseRangeLabel(value));

  useEffect(() => {
    setSelectedRange(parseRangeLabel(value));
  }, [value]);

  const handleSelect = (range) => {
    const nextRange = {
      startDate: range?.startDate || null,
      endDate: range?.endDate || null,
    };
    setSelectedRange(nextRange);
    onChange?.(formatRangeLabel(nextRange));
    onDateChange?.({ start: nextRange.startDate, end: nextRange.endDate });
  };

  return (
    <FormDateRangePicker
      date={selectedRange}
      onSelect={handleSelect}
      label={label}
      placeholder={placeholder}
      shortcuts
      {...pickerProps}
    />
  );
}

export function Modal({ title, children, onClose, footer }) {
  useEffect(() => {
    const onKeyDown = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className='fixed inset-0 z-[100] grid place-items-center bg-[#14212d]/35 p-4 backdrop-blur-[2px]'
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        role='dialog'
        aria-modal='true'
        aria-label={title}
        className='w-full max-w-md overflow-hidden rounded-2xl border border-white/70 bg-white shadow-[0_24px_70px_rgba(18,38,48,0.24)]'
      >
        <div className='flex items-start justify-between gap-4 border-b border-[#edf1f3] px-5 py-4'>
          <div>
            <p className='text-[10px] font-semibold tracking-[0.13em] text-[#728293]'>
              PULSALINK · KONFIRMASI
            </p>
            <h2 className='mt-1 text-base font-semibold text-[#263548]'>{title}</h2>
          </div>
          <button
            type='button'
            aria-label='Tutup dialog'
            onClick={onClose}
            className='grid h-8 w-8 place-items-center rounded-lg text-[#718092] hover:bg-[#f3f6f7]'
          >
            <XMarkIcon className='h-4 w-4' />
          </button>
        </div>
        <div className='px-5 py-4 text-sm leading-6 text-[#667588]'>{children}</div>
        {footer && (
          <div className='flex justify-end gap-2 border-t border-[#edf1f3] bg-[#fbfcfc] px-5 py-4'>
            {footer}
          </div>
        )}
      </section>
    </div>
  );
}

export function Tooltip({ label, children }) {
  return (
    <span className='group relative inline-flex'>
      {children}
      <span
        role='tooltip'
        className='pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 w-max -translate-x-1/2 rounded-md bg-[#253445] px-2.5 py-1.5 text-[10px] font-medium text-white opacity-0 shadow-lg transition group-hover:opacity-100 group-focus-within:opacity-100'
      >
        {label}
      </span>
    </span>
  );
}

export function Toast({ message, tone = 'success', onClose }) {
  const Icon = tone === 'error' ? XMarkIcon : CheckIcon;
  return (
    <div
      role='status'
      className='fixed bottom-5 right-5 z-[120] flex max-w-[90vw] items-center gap-3 rounded-xl border border-[#dce8e4] bg-white px-4 py-3 text-sm text-[#3f5360] shadow-[0_12px_34px_rgba(24,45,56,0.15)]'
    >
      <span
        className={`grid h-7 w-7 place-items-center rounded-full ${tone === 'error' ? 'bg-[#fff0ef] text-[#ba5953]' : 'bg-[#eaf7f2] text-[#29845f]'}`}
      >
        <Icon className='h-4 w-4' />
      </span>
      <span className='min-w-0 flex-1'>{message}</span>
      <button
        type='button'
        aria-label='Tutup notifikasi'
        onClick={onClose}
        className='grid h-6 w-6 place-items-center rounded-md text-[#8995a1] hover:bg-[#f3f6f7]'
      >
        <XMarkIcon className='h-3.5 w-3.5' />
      </button>
    </div>
  );
}

export function EmptyState({ title, description, subtitle, action, icon: Icon }) {
  return (
    <div className='grid min-h-40 place-items-center rounded-xl border border-dashed border-[#dbe4e7] bg-[#fbfcfc] px-6 py-8 text-center'>
      <div>
        {Icon && (
          <span className='mx-auto grid h-10 w-10 place-items-center rounded-xl bg-[#eff7f6] text-[#43867d]'>
            <Icon className='h-5 w-5' />
          </span>
        )}
        <h3 className='mt-3 text-sm font-semibold text-[#344255]'>{title}</h3>
        {(description || subtitle) && (
          <p className='mx-auto mt-1.5 max-w-xs text-xs leading-5 text-[#82909d]'>
            {description || subtitle}
          </p>
        )}
        {action && <div className='mt-4'>{action}</div>}
      </div>
    </div>
  );
}

export function SkeletonLine({ className = '' }) {
  return (
    <span
      aria-hidden='true'
      className={`block animate-pulse rounded-full bg-[#e9eef0] ${className}`}
    />
  );
}
